import { useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import type { CoffeeViewSource } from '@/analytics/events';
import {
  coffeeViewSourceFromNavState,
  searchIdFromNavState,
  trackCoffeeViewed,
} from '@/analytics/events';
import { CoffeePhoto } from '@/components/common/CoffeePhoto';
import { ErrorState } from '@/components/common/ErrorState';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import { useCoffee } from '@/hooks/useCoffee';
import { useImageFallback } from '@/hooks/useImageFallback';
import { useShelfToggle } from '@/hooks/useShelfToggle';

export function CoffeeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { coffee, isLoading, error, refetch } = useCoffee(id);
  // Resolve the navigation source once from router state; trusted internal
  // links declare it, everything else falls back to `direct`. Search-originated
  // links also carry a `search_id` (#448) to join the search funnel.
  const navState: unknown = useLocation().state;
  const source = coffeeViewSourceFromNavState(navState);
  const searchId = searchIdFromNavState(navState);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {isLoading && (
          <LoadingSpinner label="Loading coffee" testId="coffee-loading" />
        )}

        {!isLoading && error && (
          <ErrorState
            message="Failed to load coffee. Please try again."
            onPrimaryAction={() => void refetch()}
            testId="coffee-detail-error"
          />
        )}

        {!isLoading && !error && !coffee && (
          <ErrorState
            heading="Coffee not found"
            message="We couldn't find that coffee. It may have been removed."
            onPrimaryAction={() => void navigate('/')}
            primaryActionLabel="Browse coffees"
            testId="coffee-not-found"
          />
        )}

        {!isLoading && !error && coffee && (
          // Key on coffee.id so CoffeeDetail remounts when navigating to a
          // different coffee, preventing useShelfToggle local state from
          // leaking across coffees with matching initialIsOnShelf values.
          <CoffeeDetail key={coffee.id} coffee={coffee} source={source} searchId={searchId} />
        )}
      </div>
    </Layout>
  );
}

interface CoffeeDetailProps {
  coffee: NonNullable<ReturnType<typeof useCoffee>['coffee']>;
  source: CoffeeViewSource;
  searchId?: string;
}

function CoffeeDetail({ coffee, source, searchId }: CoffeeDetailProps) {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { isOnShelf, isToggling, toggle } = useShelfToggle(
    coffee.isOnShelf ?? false,
    coffee.id,
    'detail',
  );
  // Fire `coffee_viewed` once per coffee. CoffeeDetail is keyed on
  // coffee.id so it remounts per coffee; the ref additionally guards the
  // StrictMode double-effect in dev.
  const viewedRef = useRef<string | null>(null);
  useEffect(() => {
    if (viewedRef.current === coffee.id) return;
    viewedRef.current = coffee.id;
    trackCoffeeViewed({
      coffee_id: coffee.id,
      source,
      ...(searchId ? { search_id: searchId } : {}),
    });
    // Consume the source (and any search_id): `window.history.state` survives a
    // full-page reload, so leaving it in place would make a refresh re-emit the
    // original internal source instead of `direct`. Clearing it on first read
    // makes a refresh (and browser back to here) resolve to `direct`.
    if (source !== 'direct') {
      void navigate(`${pathname}${search}`, { replace: true, state: null });
    }
  }, [coffee.id, source, searchId, navigate, pathname, search]);
  const coffeeImageUrl = coffee.imageUrl ?? null;
  const { hasError, onError: onImageError } = useImageFallback(coffeeImageUrl);
  const showImage = coffeeImageUrl !== null && !hasError;
  const roasterName = coffee.roaster?.name ?? 'Unknown roaster';
  const roasterLocation = coffee.roaster?.location ?? null;
  const varieties = coffee.varieties && coffee.varieties.length > 0
    ? coffee.varieties.join(', ')
    : null;
  const tastingNotes = coffee.tastingNotes && coffee.tastingNotes.length > 0
    ? coffee.tastingNotes.join(', ')
    : null;

  const shelfButtonClass = `font-semibold rounded-lg transition-colors text-center ${
    isOnShelf
      ? 'border-2 border-wolisho-brown bg-wolisho-brown text-white hover:bg-wolisho-brown/90'
      : 'border-2 border-wolisho-red text-wolisho-red bg-transparent hover:bg-wolisho-red hover:text-white'
  } ${isToggling ? 'opacity-50 cursor-not-allowed' : ''}`;
  const checkInClass =
    'border-2 border-wolisho-red bg-wolisho-red text-white font-semibold rounded-lg hover:bg-wolisho-red/90 transition-colors text-center';

  const bylineOrigin = (
    <>
      <p className="text-black text-sm font-semibold mt-1">
        by <span className="text-wolisho-red">{roasterName}</span>
      </p>
      <p className="text-black text-sm font-semibold opacity-75">
        {coffee.origin}
      </p>
    </>
  );

  const header = (
    <>
      <h1 className="text-black text-3xl font-bold text-balance">
        {coffee.name}
      </h1>
      {bylineOrigin}
    </>
  );

  const metaList = (
    <dl className="grid grid-cols-1 gap-3 text-sm">
      {coffee.processingType && (
        <div>
          <dt className="font-semibold text-wolisho-brown">Process</dt>
          <dd className="text-black">{coffee.processingType}</dd>
        </div>
      )}
      {varieties && (
        <div>
          <dt className="font-semibold text-wolisho-brown">Varieties</dt>
          <dd className="text-black">{varieties}</dd>
        </div>
      )}
      {tastingNotes && (
        <div>
          <dt className="font-semibold text-wolisho-brown">Tasting notes</dt>
          <dd className="text-black">{tastingNotes}</dd>
        </div>
      )}
      {roasterLocation && (
        <div>
          <dt className="font-semibold text-wolisho-brown">Roaster location</dt>
          <dd className="text-black">{roasterLocation}</dd>
        </div>
      )}
    </dl>
  );

  return (
    <article
      className="bg-wolisho-card rounded-xl p-4 md:p-6"
      data-testid="coffee-detail"
    >
      {showImage ? (
        // Two-column split (framed tile + actions on the left, copy on the
        // right). The title spans full width above the split on mobile so long
        // names don't wrap into the narrow column; desktop keeps it in the
        // right column beside the tile. Below 400px the roaster + origin also
        // move full-width under the title (they're the longest lines), leaving
        // just the spec list in the narrow column so it doesn't get squeezed.
        <div>
          <h1 className="md:hidden text-black text-2xl font-bold text-balance mb-4">
            {coffee.name}
          </h1>
          <div className="min-[400px]:hidden mb-4">{bylineOrigin}</div>
          <div className="flex items-start gap-4 md:gap-7">
          <div className="w-36 md:w-52 shrink-0">
            <CoffeePhoto
              src={coffeeImageUrl}
              alt={coffee.name}
              sizeClassName="w-36 h-36 md:w-52 md:h-52"
              padClassName="p-2 md:p-2.5"
              onError={onImageError}
            />
            <div className="mt-3 md:mt-4 flex flex-col gap-2 md:gap-3">
              <button
                type="button"
                data-testid="shelf-button"
                disabled={isToggling}
                onClick={() => void toggle()}
                className={`w-full px-4 py-3 ${shelfButtonClass}`}
              >
                {isOnShelf ? 'On shelf' : 'Add to shelf'}
              </button>
              <Link
                to={`/coffee/${coffee.id}/check-in`}
                data-testid="check-in-button"
                className={`w-full px-4 py-3 ${checkInClass}`}
              >
                Check in
              </Link>
            </div>
          </div>

          <div className="flex-1 min-w-0 border-l border-wolisho-brown/10 pl-4 md:pl-7">
            <h1 className="hidden md:block text-black text-3xl font-bold text-balance">
              {coffee.name}
            </h1>
            <div className="hidden min-[400px]:block">{bylineOrigin}</div>
            <div className="min-[400px]:mt-6">{metaList}</div>
          </div>
          </div>
        </div>
      ) : (
        <div className="pl-2">
          {header}
          <div className="mt-6">{metaList}</div>
          <div className="mt-6 flex flex-col md:flex-row justify-center gap-3">
            <button
              type="button"
              data-testid="shelf-button"
              disabled={isToggling}
              onClick={() => void toggle()}
              className={`w-full md:w-auto px-6 py-3 ${shelfButtonClass}`}
            >
              {isOnShelf ? 'On shelf' : 'Add to shelf'}
            </button>
            <Link
              to={`/coffee/${coffee.id}/check-in`}
              data-testid="check-in-button"
              className={`w-full md:w-auto px-6 py-3 ${checkInClass}`}
            >
              Check in
            </Link>
          </div>
        </div>
      )}
    </article>
  );
}
